import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function PhotoGallery({ plant, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const photos = plant.photos || [];

  const pick = () => fileRef.current?.click();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", f);
    try {
      await api.post(`/garden/${plant.id}/photos`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added.");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (path) => {
    await api.delete(`/garden/${plant.id}/photos?path=${encodeURIComponent(path)}`);
    toast.message("Photo removed.");
    onChange?.();
  };

  const cover = plant.photo_path
    ? `${API}/files/${plant.photo_path}`
    : `${API}/catalog/${plant.catalog_id}/image`;

  return (
    <div className="space-y-3" data-testid="photo-gallery">
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={upload} data-testid="gallery-input" />

      {/* Cover */}
      <div
        className="h-72 rounded-lg overflow-hidden border border-border relative grain-overlay bg-cover bg-center group cursor-pointer"
        onClick={pick}
        style={{ backgroundImage: `url(${cover})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-card/95 via-transparent to-transparent" />
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={(e) => { e.stopPropagation(); pick(); }}
          disabled={busy}
          className="absolute top-3 right-3 gap-1.5"
          data-testid="gallery-add-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
          Add photo
        </Button>
      </div>

      {/* Timeline thumbnails */}
      {photos.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="gallery-strip">
          {photos.slice().reverse().map((p) => (
            <div key={p.path} className="relative shrink-0 group" data-testid={`gallery-thumb-${p.path}`}>
              <img
                src={`${API}/files/${p.path}`}
                alt={p.caption || "plant"}
                className="w-20 h-20 object-cover rounded-md border border-border"
              />
              <button
                onClick={() => removePhoto(p.path)}
                className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete photo"
                data-testid={`gallery-del-${p.path}`}
              >
                <Trash2 className="w-2.5 h-2.5" />
              </button>
              <div className="text-[10px] text-muted-foreground text-center mt-1">
                {(p.taken_at || "").slice(0, 10)}
              </div>
            </div>
          ))}
        </div>
      )}
      {photos.length === 0 && (
        <p className="text-xs text-muted-foreground italic">
          The illustration above is sage-generated. Add your first real photo to begin a timeline.
        </p>
      )}
    </div>
  );
}
